import React, { useEffect, useState } from "react";
import api from "../../globals/axios";
import { isNotNullOrEmptyArray } from "../../globals/utils";
import WeatherAlertCard from "./WeatherAlertCard";
import {
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Info as InfoIcon } from "@mui/icons-material";
import { useAlert } from "../Alert";

const SubscribedWeatherAlerts = () => {
  const theme = useTheme();
  const [loading, setLoading] = useState(true);
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [subscribedDistricts, setSubscribedDistricts] = useState([]);
  const [districtWeatherAlerts, setDistrictWeatherAlerts] = useState([]);
  const [selectedFilterDistrict, setSelectedFilterDistrict] = useState("");
  const { t, i18n } = useTranslation();
  const { showAlert } = useAlert();

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const { data } = await api.get("/alerts/weather-subscription");
        const subscribed = data?.data?.subscribed_districts;
        if (subscribed) {
          const districtsArray = subscribed.split(",").map((d) => d.trim());
          setSubscribedDistricts(districtsArray);
        }
      } catch (err) {
        console.error("Error loading subscription:", err);
        showAlert(
          "error",
          t("Failed to subscribed alert districts. Please try again later.")
        );
      }
    };

    fetchSubscription();
  }, []);

  useEffect(() => {
    const fetchAlerts = async () => {
      try {
        setLoading(true);
        const response = await api.get("/alerts/subscribed-weather-alerts/", {
          params: {
            lang: i18n.language,
            district: selectedFilterDistrict,
          },
        });
        if (response?.data?.data?.districts) {
          setDistrictWeatherAlerts(response.data.data.districts);
        }
      } catch (error) {
        console.error("Failed to fetch subscribed alerts", error);
        showAlert("error", t("Failed to fetch subscribed alerts."));
      } finally {
        setLoading(false);
      }
    };
    fetchAlerts();
  }, [selectedFilterDistrict, i18n.language]);

  return (
    <Box
      sx={{
        mb: 2,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center" }}>
        <Typography
          variant={isMobile ? "h6" : "h5"}
          sx={{ fontWeight: theme.typography.fontWeightMedium }}
        >
          {t("Weather Alerts based on your Subscibed Districts")}
        </Typography>
      </Box>

      {isNotNullOrEmptyArray(subscribedDistricts) && (
        <FormControl fullWidth sx={{ marginTop: "20px" }}>
          <InputLabel>Search by District</InputLabel>
          <Select
            value={selectedFilterDistrict}
            onChange={(e) => setSelectedFilterDistrict(e.target.value)}
            variant="outlined"
          >
            {subscribedDistricts.map((district) => (
              <MenuItem key={district} value={district}>
                {district}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "100%",
          mb: 2,
          gap: 2,
        }}
      >
        {loading ? (
          <CircularProgress />
        ) : !isNotNullOrEmptyArray(districtWeatherAlerts) ? (
          <Box>
            <Typography sx={{ paddingTop: 4 }}>
              {t("No alerts available.")}
            </Typography>
          </Box>
        ) : (
          <>
            {districtWeatherAlerts.map(({ district, alerts }) => (
              <Box key={district} sx={{ width: "100%", mt: 2 }}>
                {!selectedFilterDistrict && (
                  <Typography sx={{ mb: 1 }}>{district}</Typography>
                )}
                <Grid container spacing={2}>
                  {alerts.map((alert) => (
                    <Grid item xs={12} key={alert.id}>
                      <WeatherAlertCard alert={alert} />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            ))}

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mt: 3,
                gap: 0.5,
              }}
            >
              <InfoIcon sx={{ fontSize: 14, color: "#2196F3" }} />
              <Typography
                variant="caption"
                sx={{
                  color: "#666",
                  fontSize: "0.7rem",
                }}
              >
                {t(
                  "These alerts are issued by IMD and brought to you by ASQI."
                )}
              </Typography>
            </Box>
          </>
        )}
      </Box>
    </Box>
  );
};

export default SubscribedWeatherAlerts;
